import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Outlet } from 'react-router';

import { PainelDeResumo } from '@/features/acoes/componentes/PainelDeResumo';
import {
  SeloDeImpedimento,
  SeloDePrazo,
  SeloDeSituacao,
  SeloDoWorkItem,
} from '@/features/acoes/componentes/Selos';
import {
  colunasPadrao,
  rotuloDaColuna,
  useOrdemDasColunas,
  type ColunaDaTabela,
} from '@/features/acoes/colunasDaTabela';
import type { ContextoDaListaDeAcoes } from '@/features/acoes/contextoDaLista';
import {
  chavesDeAcoes,
  useEstadosDeWorkItem,
  useListaDeAcoes,
  useResumoDeAcoes,
} from '@/features/acoes/hooks/useAcoes';
import { situacoesEfetivas, situacoesForaDoPadrao, type Painel } from '@/features/acoes/paineis';
import { useSessao } from '@/features/autenticacao/useSessao';
import { diasAte } from '@/features/acoes/prazo';
import { useResolvedores } from '@/features/referencias/useReferencias';
import {
  rotuloDaAreaSesmt,
  rotuloDaPrioridade,
  rotuloDaSaudeDoPrazo,
  rotuloDaSituacao,
  rotuloDoModulo,
} from '@/features/acoes/rotulos';
import { descreverErro } from '@/lib/erros';
import { formatarData, formatarDataHora } from '@/lib/formato';
import type { AcaoNaLista, EstadoDoWorkItem, SaudePrazo, SituacaoAcao } from '@/types/api';

import estilos from './PaginaDeAcoes.module.css';

/**
 * Painel e lista de acoes. Especificacao V3.1, item 7.
 *
 * O QUE APARECE AQUI JA VEM LIMITADO PELO SERVIDOR. O escopo de area e aplicado la,
 * a partir do token, e nao existe filtro de area nesta tela que possa ampliá-lo.
 *
 * A MESMA TELA SERVE OS DOIS PAINEIS, com o recorte vindo de `painel`. O CS e o Produto
 * leem a mesma tabela com perguntas opostas — "o que eu pedi esta andando?" e "o que caiu
 * para mim?" — e o que muda entre eles e quais situacoes valem a pena olhar, nao o
 * conjunto de dados. Duas copias desta tela divergiriam na primeira correcao de coluna.
 */

/** Espera antes de buscar enquanto o usuario digita. */
const ESPERA_DA_BUSCA_EM_MS = 400;

/** O que a lista mostra depois de a gaveta criar uma ação. */
interface AcaoCriada {
  id: number;
  numero: number;
  assunto: string;
}

/**
 * O que a lista mostra depois de uma transição tirar a ação do recorte padrão.
 *
 * Existe porque a linha some da tabela no mesmo instante em que a transição dá certo, e
 * sem aviso o sucesso é indistinguível de perda: foi o que aconteceu ao concluir no Painel
 * Produto. A ação continua no painel, mas fora do que ele abre.
 */
interface AcaoQueSaiuDoPadrao {
  id: number;
  numero: number;
  assunto: string;
  situacao: SituacaoAcao;
}

interface Props {
  painel: Painel;
}

export function PaginaDeAcoes({ painel }: Props) {
  const { temPermissao } = useSessao();
  const queryClient = useQueryClient();

  /**
   * Retorno da criação.
   *
   * A gaveta fecha ao criar, e sem isto a única prova de que deu certo seria uma linha
   * nova em algum lugar de uma tabela de até 200 — que o usuário talvez nem esteja
   * vendo, porque o rascunho pode cair fora do filtro em uso.
   */
  const [criada, definirCriada] = useState<AcaoCriada | null>(null);

  /** Retorno de uma transição que tirou a ação do recorte padrão do painel. */
  const [saiuDoPadrao, definirSaiuDoPadrao] = useState<AcaoQueSaiuDoPadrao | null>(null);

  const [texto, definirTexto] = useState('');
  const [textoAplicado, definirTextoAplicado] = useState('');
  const [situacoes, definirSituacoes] = useState<SituacaoAcao[]>([]);
  /** Clientes selecionados no tile "Quem mais solicitou". Multi-selecao. */
  const [clientes, definirClientes] = useState<number[]>([]);
  /** Ordem das colunas da tabela, arrastavel no cabecalho e lembrada no navegador. */
  const {
    ordem: ordemDasColunas,
    mover: moverColuna,
    restaurar: restaurarColunas,
  } = useOrdemDasColunas();
  const [colunaArrastada, definirColunaArrastada] = useState<ColunaDaTabela | null>(null);
  const [colunaSobre, definirColunaSobre] = useState<ColunaDaTabela | null>(null);
  const ordemAlterada = ordemDasColunas.some((c, i) => c !== colunasPadrao[i]);
  /** Saudes do prazo selecionadas no tile "Prazo". Multi-selecao. */
  const [prazos, definirPrazos] = useState<SaudePrazo[]>([]);
  const [apenasImpedidas, definirApenasImpedidas] = useState(false);

  /** "Minhas tasks no Azure". Só existe no Painel Produto. */
  const [apenasMinhasTasks, definirApenasMinhasTasks] = useState(false);
  const [pagina, definirPagina] = useState(1);

  /**
   * Atrasa a busca por texto.
   *
   * Sem isso, cada tecla dispara uma requisicao e uma entrada em cache. Digitar
   * "boleto" produziria seis consultas e o resultado exibido poderia ser o de uma
   * resposta antiga que chegou por ultimo.
   */
  useEffect(() => {
    const temporizador = setTimeout(() => {
      definirTextoAplicado(texto);
      definirPagina(1);
    }, ESPERA_DA_BUSCA_EM_MS);

    return () => {
      clearTimeout(temporizador);
    };
  }, [texto]);

  /*
    SAO DOIS CONJUNTOS, E A DISTINCAO E O QUE CONSERTA O "SUMIU".

    Sem selecao vale `situacoesPadrao`: o Produto abre na fila, sem rascunho e sem
    encerrada. Com selecao vale a intersecao com `situacoesVisiveis`, que e MAIOR que o
    padrao e inclui as terminais.

    Antes os dois papeis eram o mesmo campo, e a intersecao recortava contra o proprio
    recorte inicial. O comentario aqui dizia "piso, nunca teto" e o codigo fazia
    exatamente um teto: concluir no Painel Produto tirava a acao da tela e nenhum clique
    a trazia de volta.

    O teto continua existindo, so mudou de lugar: `situacoesVisiveis` nao tem Rascunho nem
    AjusteSolicitado, entao nenhum clique traz para a fila o que e do CS.

    A selecao nao sobrevive a troca de painel porque o roteador passa `key` por painel e o
    React remonta a tela. Zerar o estado num efeito produziria uma renderizacao em cascata,
    e o proprio lint recusa.
  */
  const situacoesDaConsulta = useMemo(
    () => situacoesEfetivas(painel, situacoes),
    [situacoes, painel],
  );

  /** Tiles que o painel mostra mas nao abre por padrao. Marcados na tela. */
  const foraDoPadrao = useMemo(() => situacoesForaDoPadrao(painel), [painel]);

  const filtro = useMemo(
    () => ({
      situacoes: situacoesDaConsulta,

      // Recorte do painel, nao escolha do usuario: nao ha controle na tela que o altere.
      ...(painel.tiposDeAreaResponsavel.length === 0
        ? {}
        : { tiposDeAreaResponsavel: painel.tiposDeAreaResponsavel }),
      ...(clientes.length === 0 ? {} : { clienteIds: clientes }),
      ...(prazos.length === 0 ? {} : { saudesDoPrazo: prazos }),
      ...(textoAplicado.trim() === '' ? {} : { texto: textoAplicado.trim() }),
      ...(apenasImpedidas ? { apenasImpedidas: true } : {}),
      ...(apenasMinhasTasks ? { apenasMinhasTasks: true } : {}),
      pagina,
    }),
    [
      situacoesDaConsulta,
      painel,
      clientes,
      prazos,
      textoAplicado,
      apenasImpedidas,
      apenasMinhasTasks,
      pagina,
    ],
  );

  const { data, error, isPending, isFetching } = useListaDeAcoes(filtro);

  /*
    O resumo nao segue o filtro da tabela, mas SEGUE o recorte do painel.

    Nao segue o filtro porque clicar num tile de 12 itens zeraria os outros e nao haveria
    caminho de volta. Segue o recorte porque recorte e a fronteira do painel: sem isso o
    Painel Produto somaria as 46 acoes de outras areas nos numeros de cima e nao as
    mostraria na tabela.
  */
  const { data: resumo } = useResumoDeAcoes(painel.tiposDeAreaResponsavel, clientes);

  // Um resolvedor para a tabela inteira. Ver o comentario em Linha.
  const { nomeDaArea, nomeDoCliente, urlDoWorkItem } = useResolvedores();

  /*
    O status das tasks da PAGINA, em uma chamada.

    Depende de `data`, e nao do filtro: sao os work items que estao na tela agora. Uma chamada
    ao Azure por linha faria 50 requisicoes por render e esbarraria em limite de taxa; a API
    aceita 200 ids de uma vez, e o teto de pagina tambem e 200.
  */
  const tasksDaPagina = useMemo(
    () =>
      (data?.itens ?? [])
        .map((acao) => acao.workItem)
        .filter((numero): numero is number => numero !== null),
    [data],
  );

  const estadosDeTask = useEstadosDeWorkItem(tasksDaPagina);

  /*
    Mapa por numero de work item.

    Duas acoes podem apontar para a MESMA task — duas melhorias entregues juntas —, e por isso
    a chave e o work item e nao a acao: um mapa por acao duplicaria a mesma entrada.
  */
  const estadoPorTask = useMemo(
    () => new Map((estadosDeTask.data?.itens ?? []).map((e) => [e.workItem, e])),
    [estadosDeTask.data],
  );

  /*
    Indisponivel SO conta quando havia o que consultar.

    Sem tasks na pagina a consulta nem roda, e `data` fica indefinido — tratar isso como
    indisponivel poria "status indisponível" em toda linha de um painel sem nenhum work item
    vinculado, que e exatamente o estado de hoje.
  */
  const statusDisponivel = tasksDaPagina.length === 0 || (estadosDeTask.data?.disponivel ?? true);

  const contexto: ContextoDaListaDeAcoes = {
    aoCriar: (acao) => {
      definirCriada({ id: acao.id, numero: acao.numero, assunto: acao.assunto });

      // A ação nova entra na lista. Invalida a árvore inteira em vez de tentar inserir
      // na página certa do cache, que depende do filtro e da ordenação em uso.
      void queryClient.invalidateQueries({ queryKey: chavesDeAcoes.todas });
    },

    aoTransicionar: (acao) => {
      /*
        Só avisa quando a nova situação está FORA do que a tabela mostra agora. Avisar em
        toda transição encheria a tela de confirmação para algo que a pessoa vê acontecer
        na linha: assumir uma análise muda o selo à vista, e não precisa de aviso.

        A comparação é contra o que a consulta pediu, e não contra o padrão do painel: com
        um filtro aplicado, o que faz a linha desaparecer é o filtro em uso.
      */
      if (situacoesDaConsulta.includes(acao.situacao)) {
        definirSaiuDoPadrao(null);

        return;
      }

      definirSaiuDoPadrao({
        id: acao.id,
        numero: acao.numero,
        assunto: acao.assunto,
        situacao: acao.situacao,
      });
    },
  };

  const alternarSituacao = (situacao: SituacaoAcao) => {
    definirSituacoes((atuais) =>
      atuais.includes(situacao) ? atuais.filter((s) => s !== situacao) : [...atuais, situacao],
    );
    definirPagina(1);
  };

  const alternarCliente = (clienteId: number) => {
    definirClientes((atuais) =>
      atuais.includes(clienteId) ? atuais.filter((c) => c !== clienteId) : [...atuais, clienteId],
    );
    definirPagina(1);
  };

  const alternarPrazo = (saude: SaudePrazo) => {
    definirPrazos((atuais) =>
      atuais.includes(saude) ? atuais.filter((p) => p !== saude) : [...atuais, saude],
    );
    definirPagina(1);
  };

  return (
    <main className={estilos.pagina}>
      <div className={estilos.cabecalho}>
        <div>
          <h1 className={estilos.titulo}>{painel.titulo}</h1>
          <p className={estilos.resumo}>{painel.apoio}</p>
          {painel.fora !== undefined && <p className={estilos.fora}>{painel.fora}</p>}
        </div>

        {/* Esconder o botao e desenho de interface, nao seguranca: quem chamar
            POST /api/acoes sem a permissao recebe 403 do servidor. */}
        {/* Destino relativo: "nova" a partir de /acoes ou de /fila. Caminho fixo levaria
            quem esta na fila para o painel do CS ao criar. */}
        {temPermissao('acao.criar') && (
          <Link className={estilos.botaoPrimario} to="nova">
            Nova ação
          </Link>
        )}
      </div>

      {/* role="status" e nao "alert": criar deu certo, e interromper o leitor de tela e
          para problema. O aviso fica ate a pessoa fechar, porque some-sozinho exige ler
          rapido justamente na hora em que o olho esta na tabela. */}
      {criada !== null && (
        <p className={estilos.sucesso} role="status">
          <span>
            Rascunho <strong>#{String(criada.numero)}</strong> criado: {criada.assunto}.{' '}
            <Link to={String(criada.id)}>Abrir a ação</Link> para completar e enviar para análise.
          </span>

          <button
            type="button"
            className={estilos.fecharAviso}
            onClick={() => {
              definirCriada(null);
            }}
            aria-label="Fechar aviso do rascunho criado"
            title="Fechar aviso"
          >
            ×
          </button>
        </p>
      )}

      {/* A transicao deu certo e a linha saiu da tabela. Sem isto, os dois fatos sao
          indistinguiveis de "perdi a acao" — era o que acontecia ao concluir na fila.

          O botao de ver aplica o filtro em vez de so explicar: a pessoa acabou de encerrar
          algo e quer confirmar que encerrou, nao ler onde procurar. */}
      {saiuDoPadrao !== null && (
        <p className={estilos.sucesso} role="status">
          <span>
            <strong>#{String(saiuDoPadrao.numero)}</strong> {saiuDoPadrao.assunto} agora está{' '}
            <strong>{rotuloDaSituacao[saiuDoPadrao.situacao]}</strong> e saiu desta lista.{' '}
            <button
              type="button"
              className={estilos.acaoDoAviso}
              onClick={() => {
                definirSituacoes([saiuDoPadrao.situacao]);
                definirPagina(1);
                definirSaiuDoPadrao(null);
              }}
            >
              Mostrar {rotuloDaSituacao[saiuDoPadrao.situacao].toLocaleLowerCase('pt-BR')}
            </button>{' '}
            ou <Link to={String(saiuDoPadrao.id)}>abrir a ação</Link>.
          </span>

          <button
            type="button"
            className={estilos.fecharAviso}
            onClick={() => {
              definirSaiuDoPadrao(null);
            }}
            aria-label="Fechar aviso da ação encerrada"
            title="Fechar aviso"
          >
            ×
          </button>
        </p>
      )}

      {/* O painel vem do prototipo `Painel de Melhorias CS`, e fica ANTES da tabela porque
          e por ele que se comeca: ve-se o retrato e clica-se no que interessa. */}
      {resumo !== undefined && (
        <PainelDeResumo
          resumo={resumo}
          situacoesDoPainel={painel.situacoesVisiveis}
          situacoesForaDoPadrao={foraDoPadrao}
          selecionadas={situacoes}
          aoAlternarSituacao={alternarSituacao}
          prazosSelecionados={prazos}
          aoAlternarPrazo={alternarPrazo}
          clientesSelecionados={clientes}
          aoAlternarCliente={alternarCliente}
        />
      )}

      <section className={estilos.filtros} aria-label="Filtros">
        <div className={estilos.linhaDeFiltro}>
          <input
            className={estilos.busca}
            type="search"
            placeholder="Buscar por assunto ou descrição"
            aria-label="Buscar por assunto ou descrição"
            value={texto}
            onChange={(evento) => {
              definirTexto(evento.target.value);
            }}
          />

          <label className={estilos.caixaDeSelecao}>
            <input
              type="checkbox"
              checked={apenasImpedidas}
              onChange={(evento) => {
                definirApenasImpedidas(evento.target.checked);
                definirPagina(1);
              }}
            />
            Somente impedidas
          </label>

          {/* SO NO PAINEL PRODUTO. No Painel CS a pergunta nao faz sentido: o CS acompanha o
              que pediu, e quem esta com a task no Azure e do outro lado.

              Filtra por `System.AssignedTo` no Azure, resolvido no servidor a partir do
              e-mail do token. Se o e-mail do backlog nao for o mesmo que o Azure conhece — no
              projeto convivem @faponline.com.br e @milaneli.com.br — o resultado e vazio, e o
              aviso abaixo da tabela diz qual e-mail foi consultado. */}
          {painel.codigo === 'Produto' && (
            <label className={estilos.caixaDeSelecao}>
              <input
                type="checkbox"
                checked={apenasMinhasTasks}
                onChange={(evento) => {
                  definirApenasMinhasTasks(evento.target.checked);
                  definirPagina(1);
                }}
              />
              Minhas tasks no Azure
            </label>
          )}
        </div>

        {/* As pilulas sairam daqui: viraram os tiles do painel (situacao, prazo, cliente),
            que mostram a contagem junto do rotulo. Uma linha por filtro ativo, nomeando o
            que esta selecionado — "Filtrando por 2 situacoes" sem dizer QUAIS obrigava a
            rolar de volta ao painel para lembrar o que estava marcado. */}
        {situacoes.length > 0 && (
          <div className={estilos.linhaDeFiltro}>
            <span className={estilos.resumo}>
              Situação: {situacoes.map((s) => rotuloDaSituacao[s]).join(', ')}.
            </span>
            <button
              type="button"
              className={estilos.botao}
              onClick={() => {
                definirSituacoes([]);
                definirPagina(1);
              }}
            >
              Limpar
            </button>
          </div>
        )}

        {prazos.length > 0 && (
          <div className={estilos.linhaDeFiltro}>
            <span className={estilos.resumo}>
              Prazo: {prazos.map((p) => rotuloDaSaudeDoPrazo[p]).join(', ')}.
            </span>
            <button
              type="button"
              className={estilos.botao}
              onClick={() => {
                definirPrazos([]);
                definirPagina(1);
              }}
            >
              Limpar
            </button>
          </div>
        )}

        {clientes.length > 0 && (
          <div className={estilos.linhaDeFiltro}>
            <span className={estilos.resumo}>
              Cliente: {clientes.map((id) => nomeDoCliente(id)).join(', ')}.
            </span>
            <button
              type="button"
              className={estilos.botao}
              onClick={() => {
                definirClientes([]);
                definirPagina(1);
              }}
            >
              Limpar
            </button>
          </div>
        )}
      </section>

      {error !== null && (
        <p className={estilos.falha} role="alert">
          {descreverErro(error)}
        </p>
      )}

      {ordemAlterada && (
        <div className={estilos.restaurarColunas}>
          <button type="button" className={estilos.botao} onClick={restaurarColunas}>
            Restaurar ordem das colunas
          </button>
        </div>
      )}

      <div className={estilos.moldura}>
        <table
          className={
            isFetching && !isPending ? `${estilos.tabela} ${estilos.carregando}` : estilos.tabela
          }
        >
          <caption className="apenas-leitor-de-tela">
            Ações no escopo do usuário, com situação, prazo e área responsável
          </caption>
          <thead>
            <tr>
              {ordemDasColunas.map((coluna) => (
                <th
                  key={coluna}
                  scope="col"
                  draggable
                  className={
                    colunaSobre === coluna && colunaArrastada !== coluna
                      ? `${estilos.colunaMovel} ${estilos.colunaDestino}`
                      : estilos.colunaMovel
                  }
                  title="Arraste para mudar a posição da coluna"
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', coluna);
                    definirColunaArrastada(coluna);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    definirColunaSobre(coluna);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (colunaArrastada !== null) {
                      moverColuna(colunaArrastada, coluna);
                    }
                    definirColunaArrastada(null);
                    definirColunaSobre(null);
                  }}
                  onDragEnd={() => {
                    definirColunaArrastada(null);
                    definirColunaSobre(null);
                  }}
                >
                  {rotuloDaColuna[coluna]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.itens.map((acao) => (
              <Linha
                key={acao.id}
                acao={acao}
                ordemDasColunas={ordemDasColunas}
                nomeDaArea={nomeDaArea}
                nomeDoCliente={nomeDoCliente}
                urlDoWorkItem={urlDoWorkItem}
                estadoDaTask={acao.workItem === null ? undefined : estadoPorTask.get(acao.workItem)}
                statusDisponivel={statusDisponivel}
              />
            ))}
          </tbody>
        </table>

        {isPending && <p className={estilos.aviso}>Carregando ações...</p>}

        {data !== undefined && data.itens.length === 0 && (
          <p className={estilos.aviso}>
            {apenasMinhasTasks
              ? 'Nenhuma ação com task atribuída a você no Azure DevOps. Se você tem tasks lá, ' +
                'o e-mail do seu usuário aqui pode ser diferente do que o Azure conhece.'
              : situacoes.length > 0 ||
                  prazos.length > 0 ||
                  clientes.length > 0 ||
                  textoAplicado !== '' ||
                  apenasImpedidas
                ? 'Nenhuma ação atende a esses filtros.'
                : painel.codigo === 'Produto'
                  ? 'A fila está vazia: nada esperando análise ou entrega no seu escopo.'
                  : 'Nenhuma ação cadastrada no seu escopo ainda.'}
          </p>
        )}
      </div>

      {data !== undefined && data.totalDePaginas > 1 && (
        <nav className={estilos.paginacao} aria-label="Paginação">
          <span className={estilos.resumo}>
            Página {String(data.pagina)} de {String(data.totalDePaginas)}
          </span>

          <div className={estilos.controles}>
            <button
              type="button"
              className={estilos.botao}
              disabled={data.pagina <= 1}
              onClick={() => {
                definirPagina((atual) => Math.max(1, atual - 1));
              }}
            >
              Anterior
            </button>
            <button
              type="button"
              className={estilos.botao}
              disabled={data.pagina >= data.totalDePaginas}
              onClick={() => {
                definirPagina((atual) => atual + 1);
              }}
            >
              Próxima
            </button>
          </div>
        </nav>
      )}

      {/* A gaveta de criacao. Rota filha, e por isso a lista continua montada atras e
          nao recarrega ao abrir nem ao fechar. */}
      <Outlet context={contexto} />
    </main>
  );
}

interface LinhaProps {
  acao: AcaoNaLista;
  ordemDasColunas: ColunaDaTabela[];
  /**
   * Resolvedor recebido por prop, e nao chamado dentro da linha.
   *
   * Chamar `useResolvedores` por linha assinaria a consulta 200 vezes e reconstruiria os
   * mapas em cada uma.
   */
  nomeDaArea: (id: number | null) => string;
  nomeDoCliente: (id: number | null) => string;
  urlDoWorkItem: (numero: number | null) => string | null;
  /** Estado da task desta linha no Azure. `undefined` quando nao ha. */
  estadoDaTask: EstadoDoWorkItem | undefined;
  /** `false` quando o Azure nao respondeu para a pagina. */
  statusDisponivel: boolean;
}

function Linha({
  acao,
  ordemDasColunas,
  nomeDaArea,
  nomeDoCliente,
  urlDoWorkItem,
  estadoDaTask,
  statusDisponivel,
}: LinhaProps) {
  const clientes = acao.clienteIds.map((id) => nomeDoCliente(id));
  const enderecoDaTask = urlDoWorkItem(acao.workItem);
  // Uma celula por coluna; a ordem de exibicao vem de `ordemDasColunas`.
  const celulas: Record<ColunaDaTabela, ReactNode> = {
    numero: <td className={estilos.numero}>#{String(acao.numero)}</td>,
    assunto: (
      <td>
        {/* Relativo ao painel em que a linha esta: /acoes/3 ou /fila/3. */}
        <Link className={estilos.assunto} to={String(acao.id)}>
          {acao.assunto}
        </Link>
      </td>
    ),
    situacao: (
      <td>
        <span className={estilos.celulaDeSelos}>
          <SeloDeSituacao situacao={acao.situacao} />
          <SeloDeImpedimento visivel={acao.estaImpedida} />
        </span>
      </td>
    ),
    prazo: (
      <td>
        <span className={estilos.celulaDeSelos}>
          {formatarData(acao.prazo)}
          <SeloDePrazo
            saude={acao.saudeDoPrazo}
            prazo={acao.prazo}
            diasRestantes={diasAte(acao.prazo)}
          />
        </span>
      </td>
    ),
    areaResponsavel: <td>{nomeDaArea(acao.areaResponsavelId)}</td>,
    areaSesmt: <td>{acao.areaSesmt === null ? '—' : rotuloDaAreaSesmt[acao.areaSesmt]}</td>,
    modulo: <td>{acao.modulo === null ? '—' : rotuloDoModulo[acao.modulo]}</td>,
    prioridade: <td>{acao.prioridade === null ? '—' : rotuloDaPrioridade[acao.prioridade]}</td>,
    // Uma acao pode afetar varios clientes: o item 2 da planilha de origem tem 9.
    // O texto e truncado por CSS e o `title` carrega a lista inteira, para a coluna
    // nao empurrar as outras nem quebrar a linha em tres alturas.
    clientes: (
      <td className={estilos.clientes} title={clientes.join(', ')}>
        {clientes.join(', ')}
      </td>
    ),
    // Task do Azure. Numero sem link quando a integracao nao esta configurada: endereco
    // adivinhado levaria a pagina de erro e a culpa cairia no numero.
    task: (
      <td className={estilos.numero}>
        {acao.workItem === null ? (
          '—'
        ) : (
          <span className={estilos.celulaDeSelos}>
            {enderecoDaTask === null ? (
              `#${String(acao.workItem)}`
            ) : (
              <a href={enderecoDaTask} target="_blank" rel="noreferrer">
                #{String(acao.workItem)}
              </a>
            )}
            <SeloDoWorkItem estado={estadoDaTask} disponivel={statusDisponivel} />
          </span>
        )}
      </td>
    ),
    // `formatarDataHora`, e nao `formatarData`: este e um carimbo em UTC, e tratar
    // como data pura pularia a conversao para o fuso local.
    atualizada: <td>{formatarDataHora(acao.atualizadoEm)}</td>,
  };

  return (
    <tr>
      {ordemDasColunas.map((coluna) => (
        <Fragment key={coluna}>{celulas[coluna]}</Fragment>
      ))}
    </tr>
  );
}
